/** Partner console — Organizations: filterable list + "Create organization".
 *
 * The create/edit form lives on its own route (PartnerOrgFormPage); this page
 * is purely the list with a filter and a top-right create button. */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PartnerPageHeader } from "@/components/PartnerLayout";
import { Spinner } from "@/components/Spinner";
import { useSwitchOrg } from "@/lib/org";
import {
  useGrantSignaturePack,
  usePartnerMe,
  usePartnerOrgs,
  useResendPartnerOrgInvite,
  useResetPartnerOrgPassword,
  useSetPartnerOrgPlan,
  useSoftDeletePartnerOrg,
} from "@/lib/partner";
import {
  DEFAULT_PACK,
  type PackFields,
} from "@/modules/billing/packFields";
import { SignaturePackFields } from "@/modules/billing/SignaturePackFields";

export default function PartnerOrganizationsPage() {
  const { t } = useTranslation();
  const me = usePartnerMe();
  const orgs = usePartnerOrgs();
  const isReseller = me.data?.kind === "reseller";
  const switchOrg = useSwitchOrg();
  const softDelete = useSoftDeletePartnerOrg();
  const setPlan = useSetPartnerOrgPlan();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");

  // Reseller "Open": switch the active org + drop into the full app.
  const openOrg = (orgId: string) => {
    switchOrg.mutate(Number(orgId), {
      onSuccess: () => navigate("/dashboard"),
    });
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q || !orgs.data) return orgs.data ?? [];
    return orgs.data.filter((o) => {
      const contactEmails = (o.contacts ?? [])
        .map((c) => `${c.name} ${c.email ?? ""}`)
        .join(" ");
      return `${o.name} ${o.country ?? ""} ${o.legal_name ?? ""} ${contactEmails}`
        .toLowerCase()
        .includes(q);
    });
  }, [orgs.data, filter]);

  if (me.isLoading) return <Spinner fullscreen label={t("common.loading")} />;

  return (
    <>
      <PartnerPageHeader
        eyebrow={t("nav.partner.organizations")}
        title={t("partner.org.title")}
        description={t("partner.org.lead")}
        action={
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => navigate("/partner/organizations/new")}
          >
            + {t("partner.org.createCta")}
          </button>
        }
      />

      <section className="card space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("partner.org.listTitle")}
          </h2>
          {orgs.data && orgs.data.length > 0 && (
            <input
              className="input h-8 w-64 max-w-full text-sm"
              placeholder={t("partner.org.filterPlaceholder")}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          )}
        </div>
        {orgs.isLoading ? (
          <p className="text-sm text-nb-text-3">{t("common.loading")}</p>
        ) : !orgs.data || orgs.data.length === 0 ? (
          <p className="text-sm text-nb-text-3">{t("partner.org.noneYet")}</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-nb-text-3">{t("partner.org.noMatch")}</p>
        ) : (
          <>
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-nb-text-3">
                <tr>
                  <th className="py-1">{t("partner.org.colName")}</th>
                  <th className="py-1">{t("partner.org.colCountry")}</th>
                  <th className="py-1">{t("partner.org.colPrice")}</th>
                  <th className="py-1">{t("partner.org.colPlan")}</th>
                  <th className="py-1 text-right">{t("partner.org.campaigns")}</th>
                  <th className="py-1 text-right">{t("partner.org.sessions")}</th>
                  <th className="py-1 text-right">{t("partner.org.direct")}</th>
                  <th className="py-1 text-right">
                    {t("partner.org.colSignatures")}
                  </th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} className="border-t border-nb-border">
                    <td className="py-1.5 text-nb-text">{o.name}</td>
                    <td className="py-1.5 text-nb-text-2">{o.country || "—"}</td>
                    <td className="py-1.5 text-nb-text-2">
                      {o.custom_price_per_session_eur
                        ? `€${o.custom_price_per_session_eur}`
                        : t("partner.org.standardPrice")}
                    </td>
                    <td className="py-1.5">
                      <div className="flex items-center gap-1.5">
                        {o.pending_plan_request != null && (
                          <span
                            className="rounded bg-amber-500/15 px-1 py-0.5 text-[10px] text-amber-300"
                            title={
                              o.pending_plan_request ||
                              t("partner.org.planRequested")
                            }
                          >
                            {t("partner.org.planRequested")}
                          </span>
                        )}
                        {isReseller ? (
                          <select
                            className="input h-7 py-0 text-xs"
                            value={o.current_plan_slug || "free"}
                            disabled={setPlan.isPending}
                            onChange={(e) =>
                              setPlan.mutate({
                                orgId: o.id,
                                planSlug: e.target.value,
                              })
                            }
                            aria-label={t("partner.org.colPlan")}
                          >
                            <option value="free">
                              {t("partner.org.planFree")}
                            </option>
                            <option value="standard">
                              {t("partner.org.planStandard")}
                            </option>
                            <option value="pro">{t("partner.org.planPro")}</option>
                          </select>
                        ) : (
                          <span className="text-nb-text-2">
                            {o.current_plan_name || t("partner.org.planFree")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-1.5 text-right text-nb-text-2">
                      {o.campaigns_count}
                    </td>
                    <td className="py-1.5 text-right text-nb-text-2">
                      {o.sessions_count}
                    </td>
                    <td className="py-1.5 text-right text-nb-text-2">
                      {o.direct_sessions_count}
                    </td>
                    <td className="py-1.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <span className="font-mono text-xs text-nb-text">
                          {o.signature_credits_used ?? 0}/
                          {o.signature_credits_total ?? 0}
                        </span>
                        {isReseller && <GrantPackControl orgId={o.id} />}
                      </div>
                    </td>
                    <td className="py-1.5 text-right">
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex justify-end gap-2">
                        {o.admin_invite_pending && (
                          <OrgResendInviteButton orgId={o.id} />
                        )}
                        {isReseller && !o.admin_invite_pending && (
                          <OrgResetPasswordButton
                            orgId={o.id}
                            orgName={o.name}
                          />
                        )}
                        {isReseller && (
                          <button
                            type="button"
                            className="btn-ghost text-xs"
                            onClick={() =>
                              navigate(`/partner/organizations/${o.id}/edit`)
                            }
                          >
                            ✏️ {t("partner.org.edit")}
                          </button>
                        )}
                        {isReseller && (
                          <button
                            type="button"
                            className="btn-ghost text-xs"
                            onClick={() => openOrg(o.id)}
                            disabled={switchOrg.isPending}
                          >
                            {t("partner.org.open")} →
                          </button>
                        )}
                        <button
                          type="button"
                          className="text-xs text-nb-text-3 hover:text-cvp-danger"
                          onClick={() => {
                            if (
                              window.confirm(
                                t("partner.org.deleteConfirm", { name: o.name }),
                              )
                            ) {
                              softDelete.mutate(o.id);
                            }
                          }}
                          disabled={softDelete.isPending}
                        >
                          {t("partner.org.delete")}
                        </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!isReseller && (
              <p className="text-xs text-nb-text-3">
                {t("partner.org.commercialNote")}
              </p>
            )}
          </>
        )}
      </section>
    </>
  );
}

/** Resend the org admin's account-opening invite email. Transient inline
 * success ("Invite sent to <email>") / error state. */
function OrgResendInviteButton({ orgId }: { orgId: string }) {
  const { t } = useTranslation();
  const resend = useResendPartnerOrgInvite();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        className="btn-ghost text-xs"
        disabled={resend.isPending}
        onClick={() => {
          setErr(false);
          setSentTo(null);
          resend.mutate(orgId, {
            onSuccess: (res) => setSentTo(res.email),
            onError: () => setErr(true),
          });
        }}
      >
        {t("partner.org.resendInvite")}
      </button>
      {sentTo && (
        <span className="text-[10px] text-nb-mint" role="status">
          {t("partner.org.resendSent", { email: sentTo })}
        </span>
      )}
      {err && (
        <span className="text-[10px] text-cvp-danger" role="alert">
          {t("partner.org.resendError")}
        </span>
      )}
    </span>
  );
}

/** Reset a managed org's admin password. Confirms first (irreversible —
 * the old password stops working immediately), then shows the fresh
 * plaintext password inline with a copy button so the reseller can
 * relay it to the customer. Nothing is persisted client-side beyond
 * this component's own state; it's gone on unmount/refresh. */
function OrgResetPasswordButton({
  orgId,
  orgName,
}: {
  orgId: string;
  orgName: string;
}) {
  const { t } = useTranslation();
  const reset = useResetPartnerOrgPassword();
  const [result, setResult] = useState<{
    email: string;
    new_password: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(false);

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.new_password);
      setCopied(true);
    } catch {
      // Clipboard API can be unavailable (permissions, non-HTTPS
      // context); the password is still selectable/visible as text.
    }
  };

  if (result) {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-[10px] text-nb-text-2">
          {t("partner.org.resetPasswordSuccess", { email: result.email })}:
        </span>
        <code className="rounded bg-nb-bg-2 px-1.5 py-0.5 text-[11px] font-mono">
          {result.new_password}
        </code>
        <button
          type="button"
          className="btn-ghost text-[10px]"
          onClick={copy}
        >
          {copied
            ? t("partner.org.resetPasswordCopied")
            : t("partner.org.resetPasswordCopy")}
        </button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        className="btn-ghost text-xs"
        disabled={reset.isPending}
        onClick={() => {
          if (
            !window.confirm(
              t("partner.org.resetPasswordConfirm", { name: orgName }),
            )
          ) {
            return;
          }
          setErr(false);
          reset.mutate(orgId, {
            onSuccess: (res) => setResult(res),
            onError: () => setErr(true),
          });
        }}
      >
        {t("partner.org.resetPassword")}
      </button>
      {err && (
        <span className="text-[10px] text-cvp-danger" role="alert">
          {t("partner.org.resetPasswordError")}
        </span>
      )}
    </span>
  );
}

/** Reseller grants a custom signature pack to a managed org — a small
 * dropdown form (level + quantity + setups for QES); Grant credits the org
 * and accrues a wholesale charge. */
function GrantPackControl({ orgId }: { orgId: string }) {
  const { t } = useTranslation();
  const grant = useGrantSignaturePack();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<PackFields>(DEFAULT_PACK);
  const [done, setDone] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        className="text-nb-text-3 hover:text-nb-mint"
        title={t("partner.org.grantPack")}
        aria-label={t("partner.org.grantPack")}
        onClick={() => setOpen((v) => !v)}
      >
        {done ? "✓" : "✏️"}
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-72 space-y-2 rounded-md border border-nb-border bg-nb-surface p-3 text-left shadow-lg">
          <SignaturePackFields
            value={form}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            idPrefix={`grant-${orgId}`}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={grant.isPending || form.quantity < 1}
              onClick={() =>
                grant.mutate(
                  { orgId, ...form },
                  {
                    onSuccess: () => {
                      setDone(true);
                      setOpen(false);
                    },
                  },
                )
              }
            >
              {grant.isPending
                ? t("common.saving")
                : t("partner.org.grantPackBtn")}
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setOpen(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
